import * as React from 'react'
import { supabase } from './supabase'
import { Button, Dialog } from './ui'

function deletionMessage(error: unknown) {
  const text = error && typeof error === 'object' && 'message' in error ? String(error.message) : String(error)
  if (text.includes('UNIT_HAS_BOOKINGS')) return 'This unit has a current booking assignment or booking history without a recorded replacement. It cannot be deleted. You can retire it instead.'
  if (text.includes('UNIT_HAS_ACTIVE_HOLD')) return 'This unit has an active equipment hold. Release the hold or remove the unit from it before deleting.'
  if (text.includes('UNIT_NOT_FOUND')) return 'This unit no longer exists. Refresh the inventory list.'
  return text
}

export function InventoryDeleteAction({ unitId, assetCode, onDeleted }: {
  unitId: string
  assetCode: string
  onDeleted: () => Promise<void>
}) {
  const [loading, setLoading] = React.useState(true)
  const [blocker, setBlocker] = React.useState<string | null>(null)
  const [error, setError] = React.useState('')
  const [confirming, setConfirming] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  React.useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    const check = async () => {
      try {
        const { data, error } = await supabase.rpc('admin_inventory_delete_blocker', { p_id: unitId })
        if (error) throw error
        if (active) setBlocker(data)
      } catch (error) {
        if (active) setError(deletionMessage(error))
      } finally {
        if (active) setLoading(false)
      }
    }
    void check()
    return () => { active = false }
  }, [unitId])

  const remove = async () => {
    if (busy || loading || blocker) return
    setBusy(true)
    setError('')
    try {
      const { error } = await supabase.rpc('admin_delete_inventory_unit', { p_id: unitId })
      if (error) throw error
      await onDeleted()
    } catch (error) {
      setError(deletionMessage(error))
    } finally {
      setBusy(false)
    }
  }
  return <>
    <button type="button" className="customer-dialog-icon inventory-delete-icon" aria-label="Delete unit" title="Delete unit" aria-busy={loading || busy} disabled={loading || busy || Boolean(blocker) || Boolean(error)} onClick={() => setConfirming(true)}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18M9 6V3h6v3M5 6l1 15h12l1-15M10 10v7M14 10v7" /></svg>
    </button>
    {confirming && <Dialog isOpen onOpenChange={open => { if (!open && !busy) setConfirming(false) }} purpose="form" width="min(550px, 96vw)">
      <div className="ops-dialog">
        <h2>Delete {assetCode} permanently?</h2>
        <p>This removes the physical unit, its inventory details, maintenance entries, released hold links and released booking allocations. It cannot be undone.</p>
        <p>Manual and live bookings are preserved, along with recorded model details and assignment history.</p>
        {error && <p className="message error" role="alert">{error}</p>}
        <div className="ops-actions">
          <Button type="button" disabled={busy} onClick={() => setConfirming(false)}>Cancel</Button>
          <Button type="button" className="danger" disabled={busy} onClick={() => void remove()}>{busy ? 'Deleting…' : 'Permanently delete unit'}</Button>
        </div>
      </div>
    </Dialog>}
  </>
}
